import {Inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {County, CountySubdivisions, Court, GeometryLookupResult, SubdivisionLookupResult} from '@ee/common/models';

@Injectable({ providedIn: 'root' })
export class CountyService {
  constructor(private http: HttpClient, @Inject('environment') private environment) {
  }

  getCountyById(id: string): Observable<County | undefined> {
    return this.http.get<County | undefined>(`${this.environment.api_prefix}api/counties/${id}`);
  }

  /**
   * A blank state is answered locally rather than sent. Interpolating an empty value produces
   * `api/counties/state/` with nothing after the slash, which matches no route — Spring falls through
   * to the static-resource handler and returns a 404 that reaches the user as a failed load. Callers
   * pass a state straight off an address that may not have one yet (an intake case mid-entry, for
   * one), so "no state" is an ordinary state of the world here, not a caller bug worth a round trip.
   */
  getCountiesByState(state: string | null | undefined): Observable<County[]> {
    if (!state?.trim()) {
      return of([]);
    }
    return this.http.get<County[]>(`${this.environment.api_prefix}api/counties/state/${encodeURIComponent(state.trim())}`);
  }

  getCountiesByZipCode(zip_code: string): Observable<County[]> {
    return this.http.get<County[]>(`${this.environment.api_prefix}api/counties/zip/${zip_code}`);
  }

  getGeometryLookup(lat: number, lng: number, state: string): Observable<GeometryLookupResult> {
    return this.http.get<GeometryLookupResult>(
      `${this.environment.api_prefix}api/counties/locate-by-coordinates`,
      { params: { lat: lat.toString(), lng: lng.toString(), state } }
    );
  }

  getGeometryMatch(lat: number, lng: number, state: string): Observable<County | null> {
    return this.http.get<County | null>(
      `${this.environment.api_prefix}api/counties/match-by-coordinates`,
      { params: { lat: lat.toString(), lng: lng.toString(), state } }
    );
  }

  updateCounty(county: County): Observable<County> {
    return this.http.put<County>(`${this.environment.api_prefix}api/counties/${county.id}`, county);
  }

  /**
   * Get available courts for a county (courts in the same state not already associated).
   */
  getAvailableCourtsForCounty(countyId: string): Observable<Court[]> {
    return this.http.get<Court[]>(`${this.environment.api_prefix}api/counties/${countyId}/available-courts`);
  }

  /**
   * Add a court reference to a county.
   */
  addCourtToCounty(countyId: string, courtId: string): Observable<County> {
    return this.http.post<County>(`${this.environment.api_prefix}api/counties/${countyId}/courts/${courtId}`, {});
  }

  /**
   * Remove a court reference from a county.
   */
  removeCourtFromCounty(countyId: string, courtId: string): Observable<County> {
    return this.http.delete<County>(`${this.environment.api_prefix}api/counties/${countyId}/courts/${courtId}`);
  }

  /**
   * Get a county by ID with courts populated.
   * Use this when you need the full court data for a county.
   */
  getCountyWithCourts(countyId: string): Observable<County> {
    return this.http.get<County>(`${this.environment.api_prefix}api/counties/${countyId}`);
  }
  /**
   * Get the recommended court for a county based on subdivision matching.
   * Returns the court matched by subdivision, or the default court, or null.
   *
   * `place` narrows a multi-candidate match (EE-364 R2.4.2 — a Texas JP precinct has two places),
   * and is omitted from the query when the case has none, so a county that does not use places
   * sends exactly the request it sends today.
   */
  recommendCourt(countyId: string, subdivision: string, place?: string): Observable<Court | null> {
    const params: { [param: string]: string } = { subdivision };
    // Free text: trim, and omit a blank so a whitespace-only place cannot change matching.
    const trimmedPlace = place?.trim();
    if (trimmedPlace) {
      params['place'] = trimmedPlace;
    }
    return this.http.get<Court | null>(
      `${this.environment.api_prefix}api/counties/${countyId}/recommend-court`,
      { params }
    );
  }

  /**
   * Lookup the containing **Venue_Subdivision** for a given lat/lng/state (EE-364 R2.1.4).
   * `subdivision` is null both when nothing contains the point and when the point sits on a
   * shared boundary; `multiple_matches` is what tells those apart.
   */
  lookupSubdivision(lat: number, lng: number, state: string): Observable<SubdivisionLookupResult> {
    return this.http.get<SubdivisionLookupResult>(
      `${this.environment.api_prefix}api/jurisdictional-boundaries/lookup`,
      { params: { lat: lat.toString(), lng: lng.toString(), state } }
    );
  }

  /**
   * The subdivision selector's whole configuration for one county (EE-364 R2.1.5): whether this
   * county has a sub-county venue layer at all (`enabled`), what to call it (`label`, derived
   * server-side from `kind`) and the names to offer. A county whose state is not in
   * `subdivision-venue.supported-states`, or that has no boundary rows, answers
   * `enabled = false` — and the intake form then renders exactly as it does today.
   */
  getSubdivisions(countyId: string): Observable<CountySubdivisions> {
    return this.http.get<CountySubdivisions>(
      `${this.environment.api_prefix}api/counties/${countyId}/subdivisions`
    );
  }

}
